import { useMemo } from "react"
import { useGetActiveBudgetCategoriesQuery } from "shared/redux/endpoints/procurementV2"
import { useGetProcurementCentersQuery, useGetItemsQuery } from "shared/redux/endpoints/procurementItems"
import { useGetAuthUserCentersQuery } from "shared/redux/endpoints/auth"
import { useGetDepartmentsQuery } from "shared/redux/endpoints/procurement"
import { departmentList } from "../components/payment/create/NonPoPaymentBuilder"
import { REGION_OPTIONS } from "../constants"

// Shared option lists + query params for the filter set added to the
// OR/PO/PR list pages (from/to date, category, region, center, item —
// `supplier` is page-specific since OR doesn't have it). Centralizes the
// lookups so OrdersWorkspace / PurchasesContainer / PaymentRequestsContainer
// / ApprovalInboxContainer / OrderRequestContainer don't each re-fetch and
// re-map the same category/center/item option lists.
//
// `filters` is the flat URL-backed filter object from useProcurementFilters,
// expected to carry `fromDate`, `toDate`, `category`, `region`, `center`,
// `item` (CSV string, per FilterBar's multiselect convention) among
// whatever page-specific keys it also has.
//
// `userCenters` swaps the center list for the viewer's own centers
// (auth-user API, no region scoping) — rolled out on Orders/My Orders first.
export const useProcurementListFilters = (filters = {}, { userCenters = false } = {}) => {
  const { data: categories } = useGetActiveBudgetCategoriesQuery({ isActive: true })
  // Centers scoped to region same as the Order Request form (see
  // OrderRequestFormContainer): only North/South map to a real
  // hub.center_region bucket, so "Central" (and "All") fetch unfiltered.
  const allCentersQ = useGetProcurementCentersQuery(
    {
      region: filters.region === "North" || filters.region === "South" ? filters.region : undefined,
      size: 200,
    },
    { skip: userCenters }
  )
  const userCentersQ = useGetAuthUserCentersQuery(undefined, { skip: !userCenters })
  const centersLoading = userCenters ? userCentersQ.isFetching : allCentersQ.isFetching
  const { data: items, isFetching: itemsLoading } = useGetItemsQuery({ activeOnly: true, size: 500 })
  const { data: departmentsRaw } = useGetDepartmentsQuery()

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: String(c.id), label: c.name })),
    [categories]
  )

  const centerOptions = useMemo(
    () =>
      userCenters
        ? (userCentersQ.data || []).map((c) => ({
            value: String(c.centerId),
            label: c.centerCode ? `${c.centerName} (${c.centerCode})` : c.centerName,
          }))
        : (allCentersQ.data?.items || []).map((c) => ({
            value: String(c.centerId),
            label: c.sunCenterCode ? `${c.centerName} (${c.sunCenterCode})` : c.centerName,
          })),
    [userCenters, userCentersQ.data, allCentersQ.data]
  )

  const itemOptions = useMemo(
    () => (items || []).map((i) => ({ value: String(i.id), label: i.name || i.code })),
    [items]
  )

  const departmentOptions = useMemo(
    () =>
      departmentList(departmentsRaw).map((d) => ({
        value: String(d.teamId),
        label: d.region ? `${d.teamName} (${d.region})` : d.teamName,
      })),
    [departmentsRaw]
  )

  const queryFilters = useMemo(
    () => ({
      fromDate: filters.fromDate || undefined,
      toDate: filters.toDate || undefined,
      category: filters.category || undefined,
      region: filters.region || undefined,
      center: filters.center || undefined,
      department: filters.department || undefined,
      item: filters.item ? filters.item.split(",").filter(Boolean) : undefined,
    }),
    [filters.fromDate, filters.toDate, filters.category, filters.region, filters.center, filters.department, filters.item]
  )

  return {
    categoryOptions,
    regionOptions: REGION_OPTIONS,
    centerOptions,
    centersLoading,
    itemOptions,
    itemsLoading,
    departmentOptions,
    queryFilters,
  }
}

export default useProcurementListFilters
